import React, { useState } from "react";
import { Box, Typography, IconButton, TextField, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import PushPinIcon from "@mui/icons-material/PushPin";
import { BasicModal } from "../BasicModal/BasicModal";
import { useWindowSize } from "@uidotdev/usehooks";
import {
	TRAVEL_KIT_MODAL,
	textFieldSx,
	modalContentSx,
	primaryButtonSx,
	secondaryButtonSx,
} from "./TravelKitModalStyles";

// Template105: Travel Kit - Notas Rápidas
const Template105 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	registerScrollContainer,
	isViewer = false,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const setScrollContainerRef = (domNode) => {
		if (typeof registerScrollContainer === "function") {
			registerScrollContainer(domNode);
		}
	};

	const pageData = page.data || {};
	const [notes, setNotes] = useState(() => {
		if (pageData.notes?.length > 0) {
			return pageData.notes;
		}
		return [
			{
				id: 1,
				title: "Frases útiles",
				content:
					"• Hola / Gracias / Por favor\n• ¿Cuánto cuesta?\n• ¿Dónde está...?",
				color: "#FEF3C7",
				pinned: true,
			},
			{
				id: 2,
				title: "WiFi & Contraseñas",
				content: "Hotel: \nRestaurante: ",
				color: "#DBEAFE",
				pinned: false,
			},
			{
				id: 3,
				title: "Direcciones importantes",
				content: "• Hotel:\n• Embajada:\n• Hospital cercano:",
				color: "#FCE7F3",
				pinned: false,
			},
			{
				id: 4,
				title: "Cosas que quiero probar",
				content: "• \n• \n• ",
				color: "#D1FAE5",
				pinned: false,
			},
		];
	});

	const [editModal, setEditModal] = useState(false);
	const [editingNote, setEditingNote] = useState(null);
	const [tempNote, setTempNote] = useState({
		title: "",
		content: "",
		color: "",
	});

	const updatePageData = (newNotes) => {
		updatePage({
			...page,
			data: { ...pageData, notes: newNotes },
		});
	};

	const handleEditNote = (noteIndex) => {
		setEditingNote(noteIndex);
		setTempNote({ ...notes[noteIndex] });
		setEditModal(true);
	};

	const handleSaveNote = () => {
		const newNotes = [...notes];
		newNotes[editingNote] = { ...newNotes[editingNote], ...tempNote };
		setNotes(newNotes);
		updatePageData(newNotes);
		setEditModal(false);
	};

	const handleAddNote = () => {
		const colors = [
			"#FEF3C7",
			"#DBEAFE",
			"#FCE7F3",
			"#D1FAE5",
			"#E0E7FF",
			"#FED7AA",
		];
		const newId = Math.max(0, ...notes.map((n) => n.id)) + 1;
		const newNotes = [
			...notes,
			{
				id: newId,
				title: "Nueva nota",
				content: "",
				color: colors[newId % colors.length],
				pinned: false,
			},
		];
		setNotes(newNotes);
		updatePageData(newNotes);
	};

	const handleDeleteNote = (noteIndex) => {
		const newNotes = notes.filter((_, i) => i !== noteIndex);
		setNotes(newNotes);
		updatePageData(newNotes);
	};

	const handleTogglePin = (noteIndex) => {
		const newNotes = [...notes];
		newNotes[noteIndex].pinned = !newNotes[noteIndex].pinned;
		// Reordenar: pinned primero
		newNotes.sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
		setNotes(newNotes);
		updatePageData(newNotes);
	};

	const colorOptions = [
		{ color: "#FEF3C7", name: "Amarillo" },
		{ color: "#DBEAFE", name: "Azul" },
		{ color: "#FCE7F3", name: "Rosa" },
		{ color: "#D1FAE5", name: "Verde" },
		{ color: "#E0E7FF", name: "Lavanda" },
		{ color: "#FED7AA", name: "Naranja" },
		{ color: "#F3E8FF", name: "Púrpura" },
		{ color: "#CCFBF1", name: "Turquesa" },
	];

	const templateItemProps = {
		"data-template-item": true,
		className: "template-item",
	};

	return (
		<Box
			{...templateItemProps}
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				width: "100%",
				backgroundColor: backgroundColor || "#FFF8F5",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			{/* Header: washi strip + sticker Notas Rápidas */}
			<Box
				data-block-id="header"
				sx={{
					position: "sticky",
					top: 0,
					zIndex: 10,
					padding: isSmallScreen ? "12px 16px" : "16px 20px",
					backgroundColor: "rgb(248, 250, 252)",
					backgroundImage: `repeating-linear-gradient(
						85deg,
						transparent,
						transparent 6px,
						rgba(254, 243, 199, 0.6) 6px,
						rgba(254, 243, 199, 0.6) 9px
					)`,
					borderBottom: "3px dashed rgba(232, 180, 188, 0.5)",
					boxShadow: "0 4px 12px rgba(254, 243, 199, 0.4)",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						flexWrap: "wrap",
						gap: 1,
					}}
				>
					<Box
						sx={{
							display: "inline-flex",
							alignItems: "center",
							gap: 1.5,
							backgroundColor: "rgba(255,255,255,0.95)",
							borderRadius: "24px",
							padding: "10px 18px",
							boxShadow:
								"0 3px 14px rgba(232, 180, 188, 0.35), 0 1px 3px rgba(0,0,0,0.06)",
							border: "2px solid rgba(254, 243, 199, 0.9)",
							transform: "rotate(-1.5deg)",
						}}
					>
						<StickyNote2Icon sx={{ fontSize: 22, color: "#B8956E" }} />
						<Typography
							sx={{
								fontSize: "17px !important",
								fontWeight: "800 !important",
								color: "#5C3A45",
							}}
						>
							✿ Notas Rápidas ✿
						</Typography>
					</Box>
					<Typography
						sx={{
							backgroundColor: "rgba(255,255,255,0.9)",
							padding: "6px 12px",
							borderRadius: "14px",
							border: "2px dashed rgba(232, 180, 188, 0.4)",
							fontSize: "12px !important",
							color: "#5C3A45",
							fontWeight: 600,
						}}
					>
						{notes.length} notas
					</Typography>
				</Box>
			</Box>

			{/* Content */}
			<Box
				ref={setScrollContainerRef}
				sx={{
					flex: 1,
					minHeight: 0,
					overflow: "auto",
					WebkitOverflowScrolling: "touch",
					padding: isSmallScreen ? "12px" : "16px",
				}}
			>
				{/* Notes Grid */}
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: isSmallScreen ? "1fr" : "repeat(2, 1fr)",
						gap: 2,
					}}
				>
					{notes.map((note, noteIndex) => (
						<Box
							key={note.id}
							data-block-id={`text-${noteIndex + 1}`}
							sx={{
								transform: note.pinned
									? "rotate(-2deg)"
									: noteIndex % 2 === 0
										? "rotate(1deg)"
										: "rotate(-0.8deg)",
								transition: "transform 0.2s ease",
							}}
						>
							{/* Cinta washi arriba del post-it */}
							<Box
								sx={{
									height: 6,
									backgroundColor: "rgba(255,255,255,0.7)",
									borderRadius: "4px 4px 0 0",
									marginLeft: 1,
									marginRight: 1,
									border: "1px dashed rgba(0,0,0,0.06)",
								}}
							/>
							<Box
								sx={{
									backgroundColor: note.color,
									borderRadius: "0 0 16px 16px",
									padding: "16px",
									position: "relative",
									minHeight: "114px",
									boxShadow: note.pinned
										? "0 6px 20px rgba(232, 180, 188, 0.35), 0 2px 8px rgba(0,0,0,0.1)"
										: "0 4px 16px rgba(232, 180, 188, 0.2)",
									border: "2px solid rgba(255,255,255,0.9)",
									borderTop: "none",
									transition: "all 0.2s ease",
									"&:hover": {
										boxShadow: "0 8px 24px rgba(232, 180, 188, 0.3)",
									},
								}}
							>
								{/* Pin indicator */}
								{note.pinned && (
									<Box
										sx={{
											position: "absolute",
											top: -8,
											right: 16,
											backgroundColor: "#E8B4BC",
											borderRadius: "50%",
											width: 26,
											height: 26,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											boxShadow: "0 2px 8px rgba(232, 180, 188, 0.5)",
											border: "2px solid rgba(255,255,255,0.9)",
										}}
									>
										<PushPinIcon sx={{ fontSize: 14, color: "#fff" }} />
									</Box>
								)}

								{/* Actions */}
								<Box
									sx={{
										position: "absolute",
										top: 8,
										right: 8,
										display: "flex",
										gap: 0.5,
										opacity: 0,
										transition: "opacity 0.2s",
										".MuiBox-root:hover > &": { opacity: 1 },
									}}
									className="note-actions hide-in-viewer"
								>
									<IconButton
										{...templateItemProps}
										onClick={() => !isViewer && handleTogglePin(noteIndex)}
										size="small"
										sx={{
											backgroundColor: "rgba(0,0,0,0.1)",
											"&:hover": { backgroundColor: "rgba(0,0,0,0.2)" },
										}}
									>
										<PushPinIcon
											sx={{
												fontSize: 14,
												color: note.pinned ? "#EF4444" : "#64748B",
											}}
										/>
									</IconButton>
									<IconButton
										{...templateItemProps}
										onClick={() => !isViewer && handleEditNote(noteIndex)}
										size="small"
										sx={{
											backgroundColor: "rgba(0,0,0,0.1)",
											"&:hover": { backgroundColor: "rgba(0,0,0,0.2)" },
										}}
									>
										<EditIcon sx={{ fontSize: 14 }} />
									</IconButton>
									<IconButton
										{...templateItemProps}
										onClick={() => !isViewer && handleDeleteNote(noteIndex)}
										size="small"
										sx={{
											backgroundColor: "rgba(0,0,0,0.1)",
											"&:hover": { backgroundColor: "rgba(239,68,68,0.2)" },
										}}
									>
										<DeleteIcon sx={{ fontSize: 14 }} />
									</IconButton>
								</Box>

								{/* Note Content */}
								<Box
									{...templateItemProps}
									onClick={() => !isViewer && handleEditNote(noteIndex)}
									sx={{ cursor: isViewer ? "default" : "pointer", height: "100%" }}
								>
									<Typography
										sx={{
											fontSize: "14px !important",
											fontWeight: "700 !important",
											color: "#1E293B",
											marginBottom: 1,
										}}
									>
										{note.title}
									</Typography>
									<Typography
										sx={{
											fontSize: "13px !important",
											color: "#475569",
											whiteSpace: "pre-wrap",
											lineHeight: "1.6 !important",
										}}
									>
										{note.content || "Toca para añadir contenido..."}
									</Typography>
								</Box>

								{/* Show actions on hover */}
								<style>
									{`
									[data-template-item]:hover .note-actions {
										opacity: 1 !important;
									}
								`}
								</style>
							</Box>
						</Box>
					))}

					{/* Add Note Card */}
					<Box
						{...templateItemProps}
						className={[templateItemProps?.className, "hide-in-viewer"].filter(Boolean).join(" ")}
						onClick={() => !isViewer && handleAddNote()}
						sx={{
							backgroundColor: "#fff",
							borderRadius: "16px",
							border: "2px dashed #E8B4BC",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							padding: "30px",
							minHeight: "120px",
							cursor: "pointer",
							transition: "all 0.2s",
							boxShadow: "0 2px 12px rgba(232, 180, 188, 0.1)",
							"&:hover": {
								borderColor: "#E8B4BC",
								backgroundColor: "#FFE4EC",
							},
						}}
					>
						<AddIcon sx={{ fontSize: 28, color: "#B8959E", marginBottom: 1 }} />
						<Typography
							sx={{
								color: "#5C3A45",
								fontSize: "13px !important",
								fontWeight: 600,
							}}
						>
							✿ Nueva nota
						</Typography>
					</Box>
				</Box>
			</Box>

			{/* Edit Modal - diseño coherente con Template103/104 (oculto en viewer) */}
			<Box className="hide-in-viewer">
			<BasicModal
				width={400}
				showModal={editModal}
				handleClose={() => setEditModal(false)}
				contentSx={modalContentSx}
			>
				<Box sx={{ p: 3 }}>
					<Typography
						sx={{
							fontSize: "18px !important",
							fontWeight: 700,
							color: TRAVEL_KIT_MODAL.textPrimary,
							mb: 2,
							fontFamily: "'Inter', sans-serif",
						}}
					>
						Editar nota
					</Typography>

					<TextField
						label="Título"
						value={tempNote.title}
						onChange={(e) =>
							setTempNote({ ...tempNote, title: e.target.value })
						}
						fullWidth
						size="small"
						margin="normal"
						sx={textFieldSx}
					/>

					<TextField
						label="Contenido"
						value={tempNote.content}
						onChange={(e) =>
							setTempNote({ ...tempNote, content: e.target.value })
						}
						fullWidth
						multiline
						rows={6}
						margin="normal"
						placeholder="Escribe tu nota aquí..."
						sx={textFieldSx}
					/>

					<Typography
						sx={{
							fontSize: "13px !important",
							fontWeight: 600,
							color: TRAVEL_KIT_MODAL.textMuted,
							mt: 2,
							mb: 1.5,
							fontFamily: "'Inter', sans-serif",
						}}
					>
						Color de la nota
					</Typography>
					<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
						{colorOptions.map((option) => (
							<Box
								key={option.color}
								onClick={() =>
									setTempNote({ ...tempNote, color: option.color })
								}
								sx={{
									width: 36,
									height: 36,
									borderRadius: "8px",
									backgroundColor: option.color,
									cursor: "pointer",
									border:
										tempNote.color === option.color
											? `3px solid ${TRAVEL_KIT_MODAL.textPrimary}`
											: "3px solid transparent",
									transition: "transform 0.1s",
									"&:hover": { transform: "scale(1.1)" },
								}}
								title={option.name}
							/>
						))}
					</Box>

					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-end",
							gap: 1.5,
							mt: 3,
						}}
					>
						<Button
							onClick={() => setEditModal(false)}
							sx={secondaryButtonSx}
						>
							Cancelar
						</Button>
						<Button
							variant="contained"
							onClick={handleSaveNote}
							sx={primaryButtonSx}
						>
							Guardar
						</Button>
					</Box>
				</Box>
			</BasicModal>
			</Box>
		</Box>
	);
};

Template105.thumbnail = "/images/templates/template-tk-notes.png";

export default Template105;
